import {
  Box,
  Center,
  Heading,
  IconButton,
  Slide,
  Spacer,
  Spinner,
  Stack,
  TabPanel,
  TabPanels,
  Tabs,
} from '@chakra-ui/react'
import { faXmark } from '@fortawesome/pro-solid-svg-icons'
import { useRouter } from 'next/router'
import { useCallback, useEffect, useRef, useState } from 'react'

import chakraTokenToVar from '@/utils/chakraTokenToVar'
import { noOp } from '@/utils/helpers'

import useAPIWrapper from '@/client/hooks/useAPIWrapper'
import RPC from '@/client/rpc'

import ConversationList from '@/components/Conversation/ConversationList'
import ConversationSingle from '@/components/Conversation/ConversationSingle'
import useRealtimeAIGuideConversations from '@/components/Conversation/hooks/useRealtimeAIGuideConversations'
import useLayoutContext from '@/components/Layout/useLayoutContext'
import Icon from '@/components/UI/Icon/Icon'
import NoiseTextureOverlay from '@/components/UI/NoiseTextureOverlay'
import { NAVBAR_MOBILE_HEIGHT } from '@/components/Worlds/UI/constants'

enum GlobalConversationsPopupTab {
  List = 0,
  Single = 1,
}

export default function GlobalConversationsPopup({
  isOpen,
  onClose,
}: {
  isOpen: boolean
  onClose: () => void
}) {
  const [activeConversationUuid, setActiveConversationUuid] = useState<string | null>(null)
  const [awaitingFirstMessage, setAwaitingFirstMessage] = useState(false)
  const router = useRouter()
  const { navbarOffsetPx } = useLayoutContext()
  const { conversations, loading } = useRealtimeAIGuideConversations()
  const [activeTabIndex, setActiveTabIndex] = useState(GlobalConversationsPopupTab.List)

  const onBack = useCallback(() => {
    setActiveTabIndex(GlobalConversationsPopupTab.List)
    setActiveConversationUuid(null)
  }, [])

  const [handleNewConversation, isCreatingConversation] = useAPIWrapper(
    async () => {
      const { external_uuid } = await RPC.startAIGuideConversation({
        sourcePage: router.pathname,
      })
      setActiveConversationUuid(external_uuid)
      setActiveTabIndex(GlobalConversationsPopupTab.Single)
    },
    [router.pathname],
    'Start AI Guide conversation'
  )

  const hasAutoCreated = useRef(false)
  useEffect(() => {
    if (!isOpen) {
      hasAutoCreated.current = false
      return
    }
    if (
      !hasAutoCreated.current &&
      !loading &&
      conversations != null &&
      conversations.length === 0
    ) {
      hasAutoCreated.current = true
      setAwaitingFirstMessage(true)
      handleNewConversation().catch(noOp)
    }
  }, [isOpen, loading, conversations, handleNewConversation])

  useEffect(() => {
    if (!loading) {
      if (awaitingFirstMessage && conversations?.length === 1) {
        const hasMessages = conversations[0].last_message.length > 0
        if (hasMessages) {
          setAwaitingFirstMessage(false)
          setActiveConversationUuid(conversations[0].external_uuid)
          setActiveTabIndex(GlobalConversationsPopupTab.Single)
        }
      } else if (conversations?.length === 1) {
        setActiveConversationUuid(conversations[0].external_uuid)
        setActiveTabIndex(GlobalConversationsPopupTab.Single)
      } else if (activeConversationUuid != null) {
        setActiveTabIndex(GlobalConversationsPopupTab.Single)
      }
    }
  }, [loading, conversations, activeConversationUuid, awaitingFirstMessage])

  return (
    <Box
      position="fixed"
      bottom={{
        base: NAVBAR_MOBILE_HEIGHT,
        sm: 0,
      }}
      left={{ base: '0', md: 'unset' }}
      right={{ base: '0', md: '32px' }}
      width={{ base: 'fit-content', md: '600px' }}
      marginInline="auto"
      maxWidth="600px"
      zIndex={3}
    >
      <Slide direction="bottom" in={isOpen} style={{ position: 'relative' }}>
        <Box
          backgroundColor="bg"
          boxShadow="0px 0px 10px rgba(0, 0, 0, 0.1)"
          borderTopRadius={{ base: 0, sm: '8px' }}
          overflow="auto hidden"
          height={
            isOpen
              ? {
                  base: `calc(100vh - (${NAVBAR_MOBILE_HEIGHT}))`,
                  sm: `calc(100vh - (${navbarOffsetPx} + 60px))`,
                }
              : '0px'
          }
          transition="height 0.3s linear"
        >
          <Tabs index={activeTabIndex}>
            <TabPanels>
              <TabPanel p="0px">
                <Stack
                  bgGradient={`linear(to left, ${chakraTokenToVar('purple.solid')}, ${chakraTokenToVar('cyan.solid')})`}
                  borderBottom={`2px solid ${chakraTokenToVar('border')}`}
                  py="16px"
                  direction="row"
                  align="center"
                  width="100%"
                  minHeight="70px"
                  position="relative"
                >
                  <NoiseTextureOverlay width="100%" height="100%" />
                  {!loading ? (
                    <Heading color="gray.contrast" size="lg" fontWeight={700} ml="24px">
                      Conversations
                    </Heading>
                  ) : null}
                  <Spacer />
                  <IconButton
                    variant="icon"
                    icon={<Icon faIcon={faXmark} size="lg" />}
                    aria-label="Minimize conversation"
                    cursor="pointer"
                    onClick={onClose}
                    color="gray.contrast"
                    _hover={{ bgColor: 'transparent' }}
                    mr="16px"
                  />
                </Stack>
                <ConversationList
                  conversations={conversations}
                  loading={loading || isCreatingConversation}
                  onListItemClick={setActiveConversationUuid}
                />
              </TabPanel>
              <TabPanel p="0px">
                {activeConversationUuid == null ? null : awaitingFirstMessage ? (
                  <Center height="200px">
                    <Spinner size="lg" color="purple.solid" />
                  </Center>
                ) : (
                  <ConversationSingle
                    conversationUuid={activeConversationUuid}
                    height={{
                      base: `calc(100vh - (${NAVBAR_MOBILE_HEIGHT}))`,
                      sm: `calc(100vh - (${navbarOffsetPx} + 60px))`,
                    }}
                    headerVariant="floating"
                    onMinimize={onClose}
                    onBack={onBack}
                    hideBackButton={conversations != null && conversations.length === 1}
                  />
                )}
              </TabPanel>
            </TabPanels>
          </Tabs>
        </Box>
      </Slide>
    </Box>
  )
}
